import React, { useEffect, useState } from 'react';
import { firestore } from '../config/firebaseConfig';
import { collection, deleteDoc, doc, getDocs, updateDoc, query, where, writeBatch } from 'firebase/firestore';
import './PilulaList.css'; // Certifique-se de que o arquivo CSS está corretamente importado
import { MdDeleteForever, MdEditNote } from 'react-icons/md';
import { FaCircle, FaRegStar, FaStar } from "react-icons/fa"; // Ícones para prioridade e setas
import { IoIosArrowRoundUp, IoIosArrowRoundDown } from "react-icons/io";
import { BsFillRocketTakeoffFill } from "react-icons/bs";
import Modal from 'react-modal';
import EnviarPilula from '../components/EnviarPilula';

const PROTECTED_PILL_ID = '1728521579467'; // ID protegido que não pode ser excluído

function PilulaList({ pilulas, setPilulas, itemsPerPage, onEdit, onDelete }) {
  const tenantId = localStorage.getItem('tenantId') || 'unknownTenant'; // Obtenha o tenantId do localStorage
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState('');
  const [temasMap, setTemasMap] = useState({});  
  const [sortedPilulas, setSortedPilulas] = useState(pilulas);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  const [selectedTema, setSelectedTema] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedPillId, setSelectedPillId] = useState(null);
  const [showBloqueadas, setShowBloqueadas] = useState(false);
  const [showSimulador, setShowSimulador] = useState(false);

  const ITEMS_PER_PAGE = itemsPerPage || 10;
  const totalPages = Math.ceil(sortedPilulas.length / ITEMS_PER_PAGE);

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedPilulas = sortedPilulas.slice(startIndex, endIndex);

  const handlePageChange = (direction) => {
    if (direction === 'next' && currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    } else if (direction === 'previous' && currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const showAlert = (message) => {
    alert(message);
  };

  // Calculando o valor máximo de prioridade
  const maxPriority = pilulas.reduce((max, p) => (p.prioridade > max ? p.prioridade : max), 0);

  useEffect(() => {
    const fetchTemas = async () => {
      try {
        // Filtrar os temas pelo tenantId
        const temasCollection = query(collection(firestore, 'temas'), where('tenantId', '==', tenantId));
        const temasSnapshot = await getDocs(temasCollection);
        const temasData = temasSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));

        const temasMap = {};
        temasData.forEach(tema => {
          temasMap[tema.id] = tema.nome;
        });
        setTemasMap(temasMap);
      } catch (error) {
        console.error('Erro ao buscar temas:', error);
        setError('Houve um problema ao buscar os temas.');
      }
    };

    fetchTemas();
  }, [tenantId]);

  useEffect(() => {
    setError('');
    const fetchPilulas = async () => {
      try {
        // Filtrar as pílulas pelo tenantId
        const pilulasCollection = query(collection(firestore, 'pilulas'), where('tenantId', '==', tenantId));
        const pilulasSnapshot = await getDocs(pilulasCollection);
        const pilulasData = pilulasSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          prioridade: doc.data().prioridade !== undefined ? Number(doc.data().prioridade) : 0, // Define como 0 em vez de null
        }));
        setPilulas(pilulasData);
      } catch (error) {
        console.error('Erro ao buscar pílulas:', error);
        setError('Houve um problema ao buscar as pílulas.');
      }
    };

    fetchPilulas();
  }, [tenantId]);

  useEffect(() => {
    let sortableItems = [...pilulas];

    // Filtragem por tema e pílulas bloqueadas
    if (selectedTema) {
      sortableItems = sortableItems.filter(pilula =>
        pilula.temas && pilula.temas.includes(selectedTema)
      );
    }

    if (showBloqueadas) {
      sortableItems = sortableItems.filter(pilula => pilula.bloqueado);
    }

    const compareItems = (a, b) => {
      if (a.prioridade > 0 && b.prioridade === 0) return -1;
      if (a.prioridade === 0 && b.prioridade > 0) return 1;

      if (a.prioridade > 0 && b.prioridade > 0) {
        return a.prioridade - b.prioridade;
      }

      if (sortConfig && sortConfig.key && sortConfig.key !== 'prioridade') {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === 'createdAt' || sortConfig.key === 'dataexpiracao') {
          aValue = aValue ? new Date(aValue.seconds * 1000) : new Date(0);
          bValue = bValue ? new Date(bValue.seconds * 1000) : new Date(0);
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }

        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const dateA = new Date(a.createdAt.seconds * 1000);
      const dateB = new Date(b.createdAt.seconds * 1000);
      return dateA - dateB;
    };

    sortableItems.sort(compareItems);
    setSortedPilulas(sortableItems);
  }, [pilulas, sortConfig, selectedTema, showBloqueadas]);

  const handleDeleteClick = (id) => {
    setSelectedPillId(id);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteDoc(doc(firestore, 'pilulas', selectedPillId));
      setPilulas(prevPilulas => prevPilulas.filter(p => p.id !== selectedPillId));
      setShowConfirmModal(false);
    } catch (error) {
      console.error('Erro ao excluir pílula:', error);
      setError('Houve um problema ao excluir a pílula.');
      setShowConfirmModal(false);
    }
  };

  const handleCancelDelete = () => {
    setSelectedPillId(null);
    setShowConfirmModal(false);
  };

  const handleToggleBloqueado = async (id, currentStatus, dataexpiracao) => {
    try {
      const isExpired = dataexpiracao && new Date(dataexpiracao + 'T23:59:59Z') < new Date();
      if (currentStatus && !isExpired) {
        const pilulaRef = doc(firestore, 'pilulas', id);
        await updateDoc(pilulaRef, { bloqueado: !currentStatus });
        setPilulas(prevPilulas =>
          prevPilulas.map(pilula =>
            pilula.id === id ? { ...pilula, bloqueado: !currentStatus } : pilula
          )
        );
      } else if (!currentStatus) {
        const pilulaRef = doc(firestore, 'pilulas', id);
        await updateDoc(pilulaRef, { bloqueado: !currentStatus });
        setPilulas(prevPilulas =>
          prevPilulas.map(pilula =>
            pilula.id === id ? { ...pilula, bloqueado: !currentStatus } : pilula
          )
        );
      }
    } catch (error) {
      console.error('Erro ao atualizar o status da pílula:', error);
      setError('Houve um problema ao atualizar o status da pílula.');
    }
  };

  const handleTogglePriority = async (pilula) => {
    try {
      let newPriority;
      if (pilula.prioridade > 0) {
        newPriority = 0;
      } else {
        const maxPriority = pilulas.reduce((max, p) => (p.prioridade > max ? p.prioridade : max), 0);
        newPriority = maxPriority + 1;
      }
  
      await updateDoc(doc(firestore, 'pilulas', pilula.id), { prioridade: newPriority });
      
      setPilulas(prevPilulas =>
        prevPilulas.map(p =>
          p.id === pilula.id ? { ...p, prioridade: newPriority } : p
        )
      );
    } catch (error) {
      console.error('Erro ao atualizar prioridade:', error);
      setError('Houve um problema ao atualizar a prioridade.');
    }
  };

  const handleMovePriorityUp = async (pilula) => {
    try {
      if (pilula.prioridade > 1) {
        const novaPrioridade = pilula.prioridade - 1;
        const pilulaAcima = pilulas.find(p => p.prioridade === novaPrioridade);

        if (!pilulaAcima) {
          setError('Não há uma pílula para trocar a prioridade.');
          return;
        }

        const batch = writeBatch(firestore);
        const pilulaRef = doc(firestore, 'pilulas', pilula.id);
        batch.update(pilulaRef, { prioridade: novaPrioridade });

        const pilulaAcimaRef = doc(firestore, 'pilulas', pilulaAcima.id);
        batch.update(pilulaAcimaRef, { prioridade: pilulaAcima.prioridade + 1 });

        await batch.commit();

        setPilulas(prevPilulas =>
          prevPilulas.map(p => {
            if (p.id === pilula.id) {
              return { ...p, prioridade: novaPrioridade };
            } else if (p.id === pilulaAcima.id) {
              return { ...p, prioridade: p.prioridade + 1 };
            }
            return p;
          })
        );
      } else {
        showAlert('A pílula já está na prioridade mais alta.');
      }
    } catch (error) {
      console.error('Erro ao mover prioridade para cima:', error);
      setError('Houve um problema ao mover a prioridade para cima.');
    }
  };

  const handleMovePriorityDown = async (pilula) => {
    try {
      if (pilula.prioridade < maxPriority) {
        const novaPrioridade = pilula.prioridade + 1;
        const pilulaAbaixo = pilulas.find(p => p.prioridade === novaPrioridade);

        if (!pilulaAbaixo) {
          showAlert('Não há uma pílula abaixo para trocar a prioridade.');
          return;
        }

        const batch = writeBatch(firestore);
        const pilulaRef = doc(firestore, 'pilulas', pilula.id);
        batch.update(pilulaRef, { prioridade: novaPrioridade });

        const pilulaAbaixoRef = doc(firestore, 'pilulas', pilulaAbaixo.id);
        batch.update(pilulaAbaixoRef, { prioridade: pilulaAbaixo.prioridade - 1 });

        await batch.commit();

        setPilulas(prevPilulas =>
          prevPilulas.map(p => {
            if (p.id === pilula.id) {
              return { ...p, prioridade: novaPrioridade };
            } else if (p.id === pilulaAbaixo.id) {
              return { ...p, prioridade: p.prioridade - 1 };
            }
            return p;
          })
        );
      } else {
        showAlert('A pílula já está na prioridade mais baixa.');
      }
    } catch (error) {
      console.error('Erro ao mover prioridade para baixo:', error);
      setError('Houve um problema ao mover a prioridade para baixo.');
    }
  };

  const requestSort = (key) => {
    if (key === 'prioridade') return;
    let direction = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? 'sort-icon asc' : 'sort-icon desc';
    }
    return 'sort-icon';
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return '';

    let date;
    if (typeof dateValue.toDate === 'function') {
      date = dateValue.toDate();
    } else if (typeof dateValue === 'string' && !isNaN(Date.parse(dateValue))) {
      date = new Date(dateValue);
    } else if (typeof dateValue === 'object' && dateValue instanceof Date) {
      date = dateValue;
    } else if (typeof dateValue.seconds === 'number') {
      date = new Date(dateValue.seconds * 1000);
    } else {
      return 'Data inválida';
    }

    return date.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  };

  const isExpired = (dataexpiracao) => {
    if (!dataexpiracao) return false;
    const expirationDate = new Date(dataexpiracao + 'T23:59:59Z');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return expirationDate < today;
  };

  const handleSendPill = (pill) => {
    setSelectedPillId(pill.id);
    setShowSimulador(true);
  };

  const closeSimuladorModal = () => {
    setShowSimulador(false);
  };

  const ConfirmationModal = ({ isVisible, onConfirm, onCancel }) => {
    if (!isVisible) return null;

    return (
      <div className="confirmation-modal">
        <div className="modal-content">
          <p>Confirme a exclusão da Pílula.</p>
          <button className="confirm" onClick={onConfirm}>Confirmar</button>
          <button className="cancel" onClick={onCancel}>Cancelar</button>
        </div>
      </div>
    );
  };

  return (
    <div className="pilula-list-container">
      {error && <div className="error-message">{error}</div>}

      <ConfirmationModal
        isVisible={showConfirmModal}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

<Modal
    isOpen={showSimulador}
    onRequestClose={closeSimuladorModal}
    contentLabel="Envio Imediato de Pílula"
    className="simulador-modal"
    overlayClassName="simulador-overlay"
    style={{
        content: {
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: '1000',
            backgroundColor: '#fff',
            padding: '20px 40px', 
            borderRadius: '8px',
            maxWidth: '400px', 
            width: '90%',
            textAlign: 'center'
        },
        overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
        },
    }}>
    
    <EnviarPilula specificPillId={selectedPillId} onCancel={closeSimuladorModal} />
</Modal>

      <div className="filter-container">
        <label htmlFor="tema-filter">Filtrar por tema:</label>
        <select
          id="tema-filter"
          value={selectedTema}
          onChange={(e) => setSelectedTema(e.target.value)}
          aria-label="Selecionar tema para filtro"
        >
          <option value="">Todos os temas</option>
          {Object.entries(temasMap).map(([id, nome]) => (
            <option key={id} value={id}>
              {nome}
            </option>
          ))}
        </select>

        {selectedTema || showBloqueadas ? (
          <button
            className="clear-filter-button"
            onClick={() => {
              setSelectedTema('');
              setShowBloqueadas(false);
            }}
          >
            Limpar Filtros
          </button>
        ) : null}

        {pilulas.some((pilula) => pilula.bloqueado) && (
          <div className="filter-checkbox">
            <input
              type="checkbox"
              id="bloqueadas-filter"
              checked={showBloqueadas}
              onChange={(e) => setShowBloqueadas(e.target.checked)}
            />
            <label htmlFor="bloqueadas-filter">Mostrar Bloqueadas</label>
          </div>
        )}
      </div>

      <div className="table-container">
        <table className="pilula-table">
          <thead>
            <tr>
              <th onClick={() => requestSort('titulo')}>
                Título
                <span className={getSortIcon('titulo')}></span>
              </th>
              <th onClick={() => requestSort('temas')}>
                Tema(s)
                <span className={getSortIcon('temas')}></span>
              </th>
              <th onClick={() => requestSort('tipo')}>
                Tipo
                <span className={getSortIcon('tipo')}></span>
              </th>
              <th onClick={() => requestSort('enviado')}>
                Enviada
                <span className={getSortIcon('enviado')}></span>
              </th>
              <th onClick={() => requestSort('createdAt')}>
                Criado
                <span className={getSortIcon('createdAt')}></span>
              </th>
              <th onClick={() => requestSort('dataexpiracao')}>
                Expiração
                <span className={getSortIcon('dataexpiracao')}></span>
              </th>
              <th className="non-clickable">Prioridade</th>
              <th className="non-clickable">Status</th>
              <th className="non-clickable">Ações</th>
            </tr>
          </thead>
          <tbody>
            {paginatedPilulas.length > 0 ? (
              paginatedPilulas.map((pilula) => (
                <tr
                  key={pilula.id}
                  className={`${isExpired(pilula.dataexpiracao) ? 'expired' : ''} ${
                    pilula.prioridade > 0 ? 'highlighted' : ''
                  }`}
                  onMouseEnter={(e) => e.currentTarget.classList.add('show-actions')}
                  onMouseLeave={(e) => e.currentTarget.classList.remove('show-actions')}
                >
                  <td>{pilula.titulo}</td>
                  <td>
                    {pilula.temas && pilula.temas.length > 0
                      ? pilula.temas.map((temaId) => temasMap[temaId] || 'Tema desconhecido').join(', ')
                      : 'Nenhum tema'}
                  </td>
                  <td>{pilula.tipo}</td>
                  <td>{pilula.enviado ? 'Sim' : 'Não'}</td>
                  <td>{formatDate(pilula.createdAt)}</td>
                  <td className={isExpired(pilula.dataexpiracao) ? 'expired-date' : ''}>
                    {formatDate(pilula.dataexpiracao)}
                  </td>
                  <td className="priority-cell">
                    {!pilula.bloqueado && (
                      <div className="priority-controls">
                        {pilula.prioridade > 0 ? (
                          <>
                            <IoIosArrowRoundUp
                              size="24"
                              className={`arrow-icon ${pilula.prioridade === 1 ? 'disabled' : ''}`}
                              onClick={() => handleMovePriorityUp(pilula)}
                              title="Mover prioridade para cima"
                            />
                            <FaStar
                              size="28"
                              className="priority-icon active"
                              onClick={() => handleTogglePriority(pilula)}
                              title="Remover prioridade"
                            />
                            <IoIosArrowRoundDown
                              size="24"
                              className={`arrow-icon ${pilula.prioridade === maxPriority ? 'disabled' : ''}`}
                              onClick={() => handleMovePriorityDown(pilula)}
                              title="Mover prioridade para baixo"
                            />
                          </>
                        ) : (
                          <FaRegStar
                            size="28"
                            className="priority-icon"
                            onClick={() => handleTogglePriority(pilula)}
                            title="Marcar como prioritária"
                          />
                        )}
                      </div>
                    )}
                  </td>

                  <td className="status-cell">
                    {pilula.bloqueado ? (
                      <FaCircle
                        size="32"
                        className="action-icon play-icon"
                        onClick={() => handleToggleBloqueado(pilula.id, pilula.bloqueado, pilula.dataexpiracao)}
                        title="Desbloquear"
                      />
                    ) : (
                      <FaCircle
                        size="32"
                        className="action-icon pause-icon"
                        onClick={() => handleToggleBloqueado(pilula.id, pilula.bloqueado)}
                        title="Bloquear"
                      />
                    )}
                  </td>
                  <td className="actions-cell">
                    <div className="pilula-actions">
                      <MdEditNote
                        size="32"
                        className="action-icon edit-icon"
                        onClick={() => onEdit(pilula)}
                        title="Editar"
                      />
                      {pilula.id !== PROTECTED_PILL_ID && (
                        <>
                          <MdDeleteForever
                            size="32"
                            className="action-icon delete-icon"
                            onClick={() => handleDeleteClick(pilula.id)}
                            title="Excluir"
                          />
                          {!pilula.bloqueado && (
                            <BsFillRocketTakeoffFill
                              size="32"
                              className="action-icon-resend"
                              onClick={() => handleSendPill(pilula)}
                              title="Envio Expresso"
                            />
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9">Nenhuma pílula encontrada</td>
              </tr>
            )}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div className="pagination">
            <button onClick={() => handlePageChange('previous')} disabled={currentPage === 1}>
              Anterior
            </button>
            <span>Página {currentPage} de {totalPages}</span>
            <button onClick={() => handlePageChange('next')} disabled={currentPage === totalPages}>
              Próxima
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default PilulaList;
